import { ApolloError } from "@apollo/client";

// askr queries
export const AGGREGATE_FOR_FOCUS_RANGE_QUERY_TITLE =
  "ASKR_AGGREGATE_FOR_CUSTOM_RANGE_QUERY";
export const AGGREGATES_FOR_TREND_CHART_BARS_QUERY_TITLE =
  "ASKR_AGGREGATES_FOR_TREND_CHART_BARS_QUERY";
export const SHIFTS_QUERY_TITLE = "ASKR_SHIFTS_QUERY";

// midgard queries
export const DATA_DUMP_FOR_HUBLET_QUERY_TITLE = "MIDGARD_DATA_DUMP_FOR_HUBLET";
export const DATA_DUMP_FOR_FORECASTED_LOT_COMPLETION_TITLE =
  "DATA_DUMP_FOR_FORECASTED_LOT_COMPLETION";
export const DATA_DUMP_SINGLE_QUERY_TITLE = "MIDGARD_DATA_DUMP_SINGLE";
export const MIDGARD_SYNC_WITH_MACHEMETRY_QUERY_TITLE =
  "MIDGARD_SYNC_WITH_MACHEMETRY";
export const BATCH_PROGRESSION_QUERY_TITLE = "BATCH_PROGRESSION_QUERY";
export const DATA_DUMP_ROW_TARGET_TREND_LINE_TITLE =
  "DATA_DUMP_ROW_TARGET_TREND_LINE";
export const DATA_DUMP_ROW_SINGLE_TIMELINE_TITLE =
  "DATA_DUMP_ROW_SINGLE_TIMELINE";
export const PROCESS_GROUPS_QUERY_TITLE = "PROCESS_GROUPS_QUERY";
export const ALLOCATION_LIST_QUERY_TITLE = "ALLOCATION_LIST_QUERY";

// machemetry queries
export const CUSTOMER_TILES_QUERY_TITLE = "MACHEMETRY_CUSTOMER_TILES";

// endpoints
const ASKR_SHIFTS = "ASKR_SHIFTS";
const ASKR_AGGREGATES = "ASKR_AGGREGATES";
const MIDGARD_DATA_DUMP_HUBLET = "MIDGARD_DATA_DUMP_HUBLET";
const MIDGARD_DATA_DUMP_LOT_COMPLETION = "MIDGARD_DATA_DUMP_LOT_COMPLETION";
const MIDGARD_DATA_DUMP_SINGLE = "MIDGARD_DATA_DUMP_SINGLE";
const MIDGARD_SYNC_WITH_MACHEMETRY = "MIDGARD_SYNC_WITH_MACHEMETRY";
const MACHEMETRY_CUSTOMER_TILES = "MACHEMETRY_CUSTOMER_TILES";

/** If a query to one of these endpoints fails, we record the endpoint in local storage
 * while the ErrorManager tries several different recovery strategies,
 * incrementing "error_retries" (in local storage) each time.
 *
 * Afterwards, if we successfully fetch from the same endpoint, we reset the retries to 0.
 */
export type QueryEndpointType = {
  title: string;
  /** number of times to instantly retry in case of 401 error in handleErrorByCode.ts */
  maxInstant401Retries: number;
};

export const QUERY_ENDPOINTS: { [queryName: string]: QueryEndpointType } = {
  [AGGREGATE_FOR_FOCUS_RANGE_QUERY_TITLE]: {
    title: ASKR_AGGREGATES,
    maxInstant401Retries: 2,
  },
  [AGGREGATES_FOR_TREND_CHART_BARS_QUERY_TITLE]: {
    title: ASKR_AGGREGATES,
    maxInstant401Retries: 2,
  },
  [SHIFTS_QUERY_TITLE]: { title: ASKR_SHIFTS, maxInstant401Retries: 0 },
  [DATA_DUMP_FOR_HUBLET_QUERY_TITLE]: {
    title: MIDGARD_DATA_DUMP_HUBLET,
    maxInstant401Retries: 2,
  },
  [DATA_DUMP_FOR_FORECASTED_LOT_COMPLETION_TITLE]: {
    title: MIDGARD_DATA_DUMP_LOT_COMPLETION,
    maxInstant401Retries: 2,
  },
  [DATA_DUMP_SINGLE_QUERY_TITLE]: {
    title: MIDGARD_DATA_DUMP_SINGLE,
    maxInstant401Retries: 2,
  },
  [MIDGARD_SYNC_WITH_MACHEMETRY_QUERY_TITLE]: {
    title: MIDGARD_SYNC_WITH_MACHEMETRY,
    maxInstant401Retries: 2,
  },
  [PROCESS_GROUPS_QUERY_TITLE]: {
    title: MIDGARD_SYNC_WITH_MACHEMETRY,
    maxInstant401Retries: 2,
  },
  [CUSTOMER_TILES_QUERY_TITLE]: {
    title: MACHEMETRY_CUSTOMER_TILES,
    maxInstant401Retries: 2,
  },
};

/**
 * put error info into history.location.state to display the error page via ErrorManager
 */
export function putErrorInfoInLocationState(
  navigate: any,
  code: any,
  err: ApolloError
) {
  const {
    location: { pathname, search, hash },
  } = navigate;
  navigate(pathname + search + hash, {
    replace: true,
    state: {
      errorStatusCode: code,
      errorMessage: err.message,
      errorMoreInfo: (err.networkError as any)?.result?.errors?.[0]?.message,
    },
  });
}

export function getInstantRetriesFromLocalStorage(
  queryEndpoint: QueryEndpointType
): number {
  const retries = window.localStorage.getItem(
    error_query_instant_retries_(queryEndpoint.title)
  );
  return retries ? Number(retries) : 0;
}

export function incrementInstantRetriesLocalStorage(
  queryEndpoint: QueryEndpointType
): void {
  window.localStorage.setItem(
    error_query_instant_retries_(queryEndpoint.title),
    String(getInstantRetriesFromLocalStorage(queryEndpoint) + 1)
  );
}
function resetRetriesFromLocalStorage(queryEndpoint: QueryEndpointType): void {
  window.localStorage.removeItem(
    error_query_instant_retries_(queryEndpoint.title)
  );
}
export function resetAllRetriesFromLocalStorage(): void {
  Object.values(QUERY_ENDPOINTS).forEach(endpoint =>
    resetRetriesFromLocalStorage(endpoint)
  );
}

function error_query_instant_retries_(queryEndpointTitle: string): string {
  return `error_query_${queryEndpointTitle}_instant_retries`;
}
export type HeadersType = {
  [headerName: string]: any;
};
